import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Heart, User, Menu, X, LogOut, Sparkles, MessageSquare,
  Home as HomeIcon, Award, Wand2, Palette, Map as MapIcon,
  GitCompare, TrendingUp, Flame, Gavel,
} from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { prefetchRoute } from '../utils/prefetch';
import { useGeoBucket } from '../hooks/useGeoBucket';
import { useMarket } from '../hooks/useMarket';
import LovinLogo from './LovinLogo';
import MarketIntelligenceDropdown from './MarketIntelligenceDropdown';
import MarketSwitcher from './MarketSwitcher';
import NavDropdown from './NavDropdown';

const NEW_FEATURE_SEEN_KEY = 'lh_new_nav_seen_v1';

// "Sell" dropdown — the unique-selling-point group. Visually prominent
// because the AI Listing Assistant + private-vs-agent split is our moat
// over the incumbent Irish portals.
const SELL_ITEMS = [
  {
    to: '/agent-match',
    Icon: Gavel,
    title: 'Agent Match',
    blurb: 'Verified agents pitch to sell your home. 48h response, you pick the winner.',
    badge: 'NEW',
  },
  {
    to: '/sell-my-home',
    Icon: HomeIcon,
    title: 'Sell my home',
    blurb: 'Private owner? AI writes your listing, suggests a price — keep the commission.',
    badge: 'AI',
  },
  {
    to: '/list-with-us',
    Icon: Award,
    title: 'Estate agent area',
    blurb: 'PSRA-verified agents. Bulk listing tools, branded profile, 30 days free.',
  },
  {
    to: '/advertise',
    Icon: Sparkles,
    title: 'Advertise on lovinahome',
    blurb: 'Featured placement, sponsored area pages, agent advertising plans.',
  },
];

// "AI Tools" dropdown — consolidates Daily Doorstep, Style Quiz, Design my
// room, Compare, Yields and Map into one tidy entry so the top bar fits.
const TOOLS_ITEMS = [
  {
    to: '/home-crush',
    Icon: Heart,
    title: 'Daily Doorstep',
    blurb: 'One curated home a day — swipe-style discovery.',
    badge: 'NEW',
  },
  {
    to: '/style-quiz',
    Icon: Sparkles,
    title: 'Style Quiz',
    blurb: 'Find homes that match your taste in 60 seconds.',
  },
  {
    to: '/design-my-room',
    Icon: Palette,
    title: 'Design my room',
    blurb: 'AI re-styles any room photo. Share before/after.',
    badge: 'AI',
  },
  {
    to: '/compare',
    Icon: GitCompare,
    title: 'Compare',
    blurb: 'Pin up to 3 homes side-by-side with full-cost + AI verdicts.',
  },
  {
    to: '/yields',
    Icon: TrendingUp,
    title: 'Rental Yields',
    blurb: 'Gross + net yields by area — find your next BTL.',
  },
  {
    to: '/map',
    Icon: MapIcon,
    title: 'Map search',
    blurb: 'Every listing on one map — viewport-synced filters.',
  },
  {
    to: '/map?draw=1',
    Icon: MapIcon,
    title: 'Draw your area',
    blurb: 'Trace any shape on the map — only homes inside show up.',
  },
];

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showNewDot, setShowNewDot] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Transparent-on-top navbar only on the homepage (where the full-bleed
  // hero image sits behind it). On every other route we keep the navbar
  // solid white so content always has a stable background.
  const isHome = location.pathname === '/';
  const transparent = isHome && !scrolled && !mobileMenuOpen;

  useEffect(() => {
    if (!isHome) {
      setScrolled(false);
      return undefined;
    }
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);

  // First-visit "new feature" pulse on Daily Doorstep + Style Quiz links.
  // Disappears the moment the user clicks either link (or visits those
  // routes directly).
  useEffect(() => {
    try {
      const seen = localStorage.getItem(NEW_FEATURE_SEEN_KEY);
      setShowNewDot(seen !== '1');
    } catch {
      setShowNewDot(false);
    }
  }, []);

  useEffect(() => {
    if (location.pathname === '/home-crush' || location.pathname === '/style-quiz') {
      acknowledgeNewFeatures();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const acknowledgeNewFeatures = () => {
    try {
      localStorage.setItem(NEW_FEATURE_SEEN_KEY, '1');
    } catch {
      /* noop */
    }
    setShowNewDot(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Tokenised classes — keeps the JSX below readable.
  const navBg = transparent
    ? 'bg-transparent border-transparent'
    : 'bg-white border-gray-200 shadow-sm';
  const linkClass = transparent
    ? 'text-white/90 hover:text-white'
    : 'text-gray-700 hover:text-[#7042cc]';
  const linkBase = `font-medium transition-colors text-[13px] whitespace-nowrap ${linkClass}`;
  const mobileBtnClass = transparent ? 'text-white' : 'text-gray-700';

  // Market-aware navigation. `useMarket()` reads the URL prefix so a UK
  // visitor on /uk/* stays inside /uk when they click Buy/Rent/Sell/etc.
  const market = useMarket();
  const p = (path) => market.withPrefix(path);

  // Geo-aware destinations — UK visitors get the HM Land Registry view
  // for "Sold" instead of the IE Property Price Register view. Routes
  // unified in Phase 4 — same component handles both URL families. On
  // /us this must also stay under /us/sold rather than jumping to /sold.
  const { bucket } = useGeoBucket();
  const soldHref = market.isUK || bucket === 'UK' ? '/uk/sold' : p('/sold');

  // Sell / AI Tools dropdown item lists — build market-prefixed copies so
  // clicking any entry lands inside the current market's URL family.
  const sellItems = SELL_ITEMS.map((it) => ({ ...it, to: p(it.to) }));
  const toolsItems = TOOLS_ITEMS.map((it) => ({ ...it, to: p(it.to) }));

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-[1000] border-b transition-colors duration-300 ${navBg}`}
      style={{ top: 'var(--demo-banner-h, 0px)' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo — smaller on phones (28px) so it fits the narrow navbar
              alongside the hamburger; full size on tablet+ (36px). */}
          <Link to={p('/')} className="flex items-center shrink-0" data-testid="navbar-logo">
            <span className="md:hidden">
              <LovinLogo word="Home" size={28} onDark={transparent} />
            </span>
            <span className="hidden md:inline-flex">
              <LovinLogo word="Home" size={36} onDark={transparent} />
            </span>
          </Link>

          {/* Desktop Navigation — consolidated. 6 top-level items + Sell ▾ CTA */}
          <div className="hidden lg:flex items-center gap-x-3 xl:gap-x-5">
            <Link to={p('/buy')} className={linkBase}>Buy</Link>
            <Link to={p('/rent')} className={linkBase}>Rent</Link>
            <Link to={p('/share')} onMouseEnter={() => prefetchRoute('/share')} data-testid="navbar-share-link" className={linkBase}>
              Share
            </Link>
            <Link to={p('/new-homes')} className={linkBase}>New Homes</Link>
            <Link to={soldHref} className={linkBase} data-testid="navbar-sold-link">Sold</Link>

            <MarketIntelligenceDropdown linkClass={linkBase} transparent={transparent} />

            <NavDropdown
              label="AI Tools"
              Icon={Wand2}
              items={toolsItems}
              linkClass={linkBase}
              transparent={transparent}
              testId="nav-tools"
              accent="purple"
            />

            <NavDropdown
              label="Sell"
              Icon={HomeIcon}
              items={sellItems}
              linkClass={linkBase}
              transparent={transparent}
              testId="nav-sell"
              accent="amber"
              triggerHighlight
            />

            <Link to={p('/pricing')} onMouseEnter={() => prefetchRoute('/pricing')} data-testid="navbar-pricing-link" className={linkBase}>
              Pricing
            </Link>
          </div>

          {/* Right side actions */}
          <div className="hidden lg:flex items-center space-x-2 xl:space-x-3">
            <MarketSwitcher transparent={transparent} />
            {isAuthenticated ? (
              <>
                <BiddingNavLink transparent={transparent} />
                <InboxNavLink transparent={transparent} />
                <Link to="/saved" onMouseEnter={() => prefetchRoute('/saved')}>
                  <Button variant="ghost" size="sm" className={transparent ? 'text-white hover:bg-white/10 hover:text-white' : ''}>
                    <Heart className="h-5 w-5 mr-2" />
                    Saved
                  </Button>
                </Link>
                <Link to="/dashboard" onMouseEnter={() => prefetchRoute('/dashboard')}>
                  <Button variant="ghost" size="sm" className={transparent ? 'text-white hover:bg-white/10 hover:text-white' : ''}>
                    Account
                  </Button>
                </Link>
                <Link to={p('/add-property')} onMouseEnter={() => prefetchRoute('/add-property')}>
                  <Button size="sm" className="bg-[#7042cc] hover:bg-[#5b35a8] rounded-xl">
                    Add Property
                  </Button>
                </Link>
                <Button variant="ghost" size="sm" onClick={handleLogout} className={transparent ? 'text-white hover:bg-white/10 hover:text-white' : ''}>
                  <LogOut className="h-5 w-5" />
                </Button>
              </>
            ) : (
              <>
                <Link to={p('/login')} onMouseEnter={() => prefetchRoute('/login')} data-testid="navbar-signin-link">
                  <Button variant="ghost" size="sm" className={transparent ? 'text-white hover:bg-white/10 hover:text-white' : ''}>
                    Sign In
                  </Button>
                </Link>
                <Link to={p('/register')} onMouseEnter={() => prefetchRoute('/register')} data-testid="navbar-signup-link">
                  <Button size="sm" className={transparent ? 'bg-white text-[#5b35a8] hover:bg-white/90 rounded-xl' : 'bg-[#7042cc] hover:bg-[#5b35a8] rounded-xl'}>
                    Sign Up
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className={`lg:hidden p-2 ${mobileBtnClass}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 max-h-[calc(100vh-80px)] overflow-y-auto overscroll-contain">
          <div className="px-4 py-3 pb-8 space-y-3">
            <Link to={p('/buy')} onClick={() => setMobileMenuOpen(false)} className="block py-2 text-gray-700 hover:text-[#7042cc]">
              Buy
            </Link>
            <Link to={p('/rent')} onClick={() => setMobileMenuOpen(false)} className="block py-2 text-gray-700 hover:text-[#7042cc]">
              Rent
            </Link>
            <Link to={p('/share')} onClick={() => setMobileMenuOpen(false)} className="block py-2 text-gray-700 hover:text-[#7042cc]">
              Share
            </Link>
            <Link to={p('/new-homes')} onClick={() => setMobileMenuOpen(false)} className="block py-2 text-gray-700 hover:text-[#7042cc]">
              New Homes
            </Link>
            <Link to={soldHref} onClick={() => setMobileMenuOpen(false)} className="block py-2 text-gray-700 hover:text-[#7042cc]" data-testid="navbar-mobile-sold-link">
              Sold
            </Link>
            <MarketIntelligenceDropdown mobile onItemClick={() => setMobileMenuOpen(false)} />

            <NavDropdown
              label="AI Tools"
              items={toolsItems}
              mobile
              onItemClick={() => setMobileMenuOpen(false)}
              testId="nav-tools"
              accent="purple"
            />

            <NavDropdown
              label="Sell on lovinahome"
              items={sellItems}
              mobile
              onItemClick={() => setMobileMenuOpen(false)}
              testId="nav-sell"
              accent="amber"
            />

            <Link to={p('/pricing')} onClick={() => setMobileMenuOpen(false)} className="block py-2 text-gray-700 hover:text-[#7042cc]">
              Pricing
            </Link>
            <div className="pt-3 border-t border-gray-200 space-y-2">
              <div className="flex justify-end pb-1">
                <MarketSwitcher />
              </div>
              {isAuthenticated ? (
                <>
                  <Link to="/saved" onClick={() => setMobileMenuOpen(false)} className="block">
                    <Button variant="outline" className="w-full">
                      <Heart className="h-5 w-5 mr-2" />
                      Saved Properties
                    </Button>
                  </Link>
                  <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)} className="block">
                    <Button variant="outline" className="w-full">
                      <User className="h-5 w-5 mr-2" />
                      My Account
                    </Button>
                  </Link>
                  <Link to={p('/add-property')} onClick={() => setMobileMenuOpen(false)} className="block">
                    <Button className="w-full bg-[#7042cc] hover:bg-[#5b35a8] rounded-xl">
                      Add Property
                    </Button>
                  </Link>
                  <Button variant="outline" className="w-full" onClick={() => { setMobileMenuOpen(false); handleLogout(); }}>
                    <LogOut className="h-5 w-5 mr-2" />
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Link to={p('/login')} onClick={() => setMobileMenuOpen(false)} className="block">
                    <Button variant="outline" className="w-full">
                      Sign In
                    </Button>
                  </Link>
                  <Link to={p('/register')} onClick={() => setMobileMenuOpen(false)} className="block">
                    <Button className="w-full bg-[#7042cc] hover:bg-[#5b35a8] rounded-xl">
                      Sign Up
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

/**
 * NavbarWithSpacer — wraps `<Navbar/>` and on every non-home route
 * appends an 80 px spacer (matches the navbar's `h-20`) so page
 * content isn't hidden behind the fixed navbar. The home route handles
 * its own spacing via the full-bleed hero image that sits behind the
 * transparent navbar.
 */
const NavbarWithSpacer = () => {
  const { pathname } = useLocation();
  return (
    <>
      <Navbar />
      {pathname !== '/' && <div aria-hidden className="h-20" />}
    </>
  );
};

/**
 * Inbox nav link with live unread-count badge.
 * Polls /api/chats every 30s when the user is signed in. Cheap and reliable.
 */
const InboxNavLink = ({ transparent }) => {
  const [unread, setUnread] = React.useState(0);
  React.useEffect(() => {
    let alive = true;
    const fetchUnread = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const d = await r.json();
        if (!alive) return;
        const me = d.me_user_id;
        const total = (d.chats || []).reduce((acc, c) => {
          const u = c.agent_user_id === me ? c.agent_unread_count : c.buyer_unread_count;
          return acc + (u || 0);
        }, 0);
        setUnread(total);
      } catch (_e) { /* ignore */ }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  return (
    <Link to="/inbox" data-testid="navbar-inbox-link">
      <Button variant="ghost" size="sm"
              className={`relative ${transparent ? 'text-white hover:bg-white/10 hover:text-white' : ''}`}>
        <MessageSquare className="h-5 w-5 mr-2" />
        Inbox
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 bg-[#7042cc] text-white text-[10px] font-semibold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
            data-testid="navbar-inbox-unread">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </Button>
    </Link>
  );
};

/**
 * Agent-only "Bidding" nav link with a live needs-attention badge.
 *
 * Polls `/api/agent/bidding-overview/unread-count` every 60s. Hidden
 * automatically if the endpoint returns total=0 AND the user owns no
 * listings (server returns 0/0/0). The link itself is universally rendered
 * for authenticated users — non-owners simply never see the badge.
 */
const BiddingNavLink = ({ transparent }) => {
  const [total, setTotal] = React.useState(0);
  const [hasAny, setHasAny] = React.useState(false);
  React.useEffect(() => {
    let alive = true;
    const fetchCount = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/agent/bidding-overview/unread-count`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!r.ok) return;
        const d = await r.json();
        if (!alive) return;
        setTotal(d.total || 0);
        setHasAny((d.total || 0) > 0);
      } catch (_e) { /* ignore */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    const onFocus = () => fetchCount();
    window.addEventListener('focus', onFocus);
    return () => { alive = false; clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, []);
  // Only render the link if there's actually something to show. Avoids
  // cluttering the navbar for buyers / casual users.
  if (!hasAny) return null;
  return (
    <Link to="/dashboard/bidding" data-testid="navbar-bidding-link">
      <Button variant="ghost" size="sm"
              className={`relative ${transparent ? 'text-white hover:bg-white/10 hover:text-white' : ''}`}>
        <Gavel className="h-5 w-5 mr-2" />
        Bidding
        {total > 0 && (
          <span
            className="absolute -top-1 -right-1 bg-amber-500 text-white text-[10px] font-semibold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
            data-testid="navbar-bidding-unread">
            {total > 99 ? '99+' : total}
          </span>
        )}
      </Button>
    </Link>
  );
};

export default NavbarWithSpacer;
